import React from 'react';

/**
 * Button — primary action control for the audit platform.
 * Variants: primary (brand), secondary (outline), ghost, danger.
 * Sizes: sm, md, lg. Optional leading/trailing icon nodes.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled = false,
  fullWidth = false,
  leadingIcon = null,
  trailingIcon = null,
  children,
  style = {},
  ...rest
}) {
  const sizes = {
    sm: { height: 32, padding: '0 12px', font: 'var(--text-sm)', gap: 6, radius: 'var(--radius-sm)' },
    md: { height: 40, padding: '0 16px', font: 'var(--text-base)', gap: 8, radius: 'var(--radius-sm)' },
    lg: { height: 48, padding: '0 22px', font: 'var(--text-lg)', gap: 8, radius: 'var(--radius-md)' },
  };
  const variants = {
    primary: { background: 'var(--brand-600)', color: 'var(--text-on-brand)', border: '1px solid var(--brand-600)' },
    secondary: { background: 'var(--white)', color: 'var(--text-strong)', border: '1px solid var(--border-strong)' },
    ghost: { background: 'transparent', color: 'var(--text-brand)', border: '1px solid transparent' },
    danger: { background: 'var(--crit-600)', color: 'var(--white)', border: '1px solid var(--crit-600)' },
  };
  const s = sizes[size] || sizes.md;
  const v = variants[variant] || variants.primary;

  return (
    <button
      type={type}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: s.gap, height: s.height, padding: s.padding,
        width: fullWidth ? '100%' : 'auto',
        fontFamily: 'var(--font-sans)', fontSize: s.font, fontWeight: 'var(--fw-semibold)',
        lineHeight: 1, letterSpacing: '0.005em', whiteSpace: 'nowrap',
        borderRadius: s.radius, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background var(--dur-fast) var(--ease-standard), box-shadow var(--dur-fast) var(--ease-standard), transform var(--dur-fast) var(--ease-standard)',
        ...v, ...style,
      }}
      onMouseDown={(e) => { if (!disabled) e.currentTarget.style.transform = 'translateY(0.5px)'; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
      {...rest}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
}
